import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Check, X, Sparkles, Building2, Users, Package,
  ArrowRight, Crown, Zap
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const Pricing = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({ architect_plans: [], supplier_plans: [] });
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
    if (isAuthenticated) {
      fetchCurrentSubscription();
    }
  }, [isAuthenticated]);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/plans`);
      setPlans(res.data);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      // Use getAuthHeaders from useAuth hook
      const headers = await getAuthHeaders();
      if (!headers) {
        console.log('No auth headers available');
        return;
      }
      const res = await axios.get(`${API}/subscriptions/my-subscription`, { headers });
      setCurrentPlan(res.data.subscription?.plan_id);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    }
  };

  const handleSelectPlan = async (plan) => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/pricing');
      return;
    }

    // Free plan - no checkout needed
    if (plan.plan_id === 'free') {
      toast.info('You are already on the free plan!');
      return;
    }

    // Check if user is already on this plan
    if (currentPlan === plan.plan_id) {
      toast.info('You are already subscribed to this plan!');
      return;
    }

    // If plan has trial and user hasn't used it
    if (plan.trial_days > 0 && currentPlan === 'free') {
      try {
        setProcessingPlan(plan.plan_id);
        const headers = await getAuthHeaders();
        await axios.post(`${API}/subscriptions/start-trial/${plan.plan_id}`, {}, { headers });
        toast.success(`${plan.trial_days}-day free trial started!`);
        navigate('/account/subscription');
        return;
      } catch (err) {
        // If trial already used, proceed to checkout
        if (err.response?.status !== 400) {
          toast.error('Failed to start trial');
          setProcessingPlan(null);
          return;
        }
      }
    }

    // Create checkout session
    try {
      setProcessingPlan(plan.plan_id);
      const headers = await getAuthHeaders();
      const res = await axios.post(`${API}/subscriptions/checkout`, {
        plan_id: plan.plan_id,
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      }, { headers });

      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error('Failed to create checkout session');
      setProcessingPlan(null);
    }
  };

  const getPrice = (plan) => {
    if (plan.pricing.monthly_price === 0 && plan.pricing.yearly_price === 0) {
      return { price: 0, period: '' };
    }
    if (billingCycle === 'yearly') {
      return { 
        price: plan.pricing.yearly_price, 
        period: '/year',
        monthly: Math.round(plan.pricing.yearly_price / 12)
      };
    }
    return { price: plan.pricing.monthly_price, period: '/month' };
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'pro': return <Crown className="w-6 h-6" />;
      case 'studio': return <Building2 className="w-6 h-6" />;
      case 'supplier': return <Package className="w-6 h-6" />;
      default: return <Sparkles className="w-6 h-6" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-20 pt-32">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-medium text-stone-900 mb-4">
            Choose the right plan for you
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto mb-8">
            Start free and upgrade as your business grows. All plans include access to our material library and visual search.
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-stone-100 p-1.5 rounded-full">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-white text-stone-900 shadow-sm' 
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'yearly' 
                  ? 'bg-white text-stone-900 shadow-sm' 
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs text-green-600 font-semibold">Save 20%</span>
            </button>
          </div>
        </div>

        {/* Architect/Designer Plans */}
        <div className="mb-20">
          <h2 className="text-sm uppercase tracking-widest text-stone-500 text-center mb-8">
            For Architects & Interior Designers
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {plans.architect_plans.map((plan) => {
              const priceInfo = getPrice(plan);
              const isCurrentPlan = currentPlan === plan.plan_id;
              const isPopular = plan.is_popular;
              
              return (
                <div 
                  key={plan.plan_id}
                  className={`relative bg-white border-2 rounded-xl p-8 transition-all hover:shadow-lg ${
                    isPopular 
                      ? 'border-primary shadow-lg scale-105' 
                      : 'border-stone-200 hover:border-stone-300'
                  } ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}`}
                >
                  {/* Popular Badge */}
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-primary-foreground text-xs font-semibold px-4 py-1.5 rounded-full">
                        MOST POPULAR
                      </span>
                    </div>
                  )}
                  
                  {/* Current Plan Badge */}
                  {isCurrentPlan && (
                    <div className="absolute -top-4 right-4">
                      <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        CURRENT
                      </span>
                    </div>
                  )}
                  
                  {/* Plan Header */}
                  <div className="text-center mb-6">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-4 ${
                      isPopular ? 'bg-primary/10 text-primary' : 'bg-stone-100 text-stone-600'
                    }`}>
                      {getPlanIcon(plan.plan_id)}
                    </div>
                    <h3 className="text-xl font-semibold text-stone-900">{plan.name}</h3>
                    <p className="text-sm text-stone-500 mt-1">{plan.description}</p>
                  </div>
                  
                  {/* Price */}
                  <div className="text-center mb-6">
                    {priceInfo.price === 0 ? (
                      <div className="text-4xl font-bold text-stone-900">Free</div>
                    ) : (
                      <>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-stone-900">${priceInfo.price}</span>
                          <span className="text-stone-500">{priceInfo.period}</span>
                        </div>
                        {billingCycle === 'yearly' && priceInfo.monthly && (
                          <p className="text-sm text-stone-500 mt-1">
                            ${priceInfo.monthly}/month billed annually
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Trial Badge */}
                  {plan.trial_days > 0 && !isCurrentPlan && currentPlan === 'free' && (
                    <div className="text-center mb-4">
                      <span className="text-sm text-primary font-medium">
                        {plan.trial_days}-day free trial
                      </span>
                    </div>
                  )}
                  
                  {/* CTA Button */}
                  <Button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={processingPlan === plan.plan_id || isCurrentPlan}
                    className={`w-full mb-6 ${
                      isPopular 
                        ? 'bg-primary hover:bg-primary/90' 
                        : 'bg-stone-900 hover:bg-stone-800'
                    }`}
                    data-testid={`select-plan-${plan.plan_id}`}
                  >
                    {processingPlan === plan.plan_id ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : plan.trial_days > 0 && currentPlan === 'free' ? (
                      `Start ${plan.trial_days}-day Trial`
                    ) : plan.plan_id === 'free' ? (
                      'Get Started'
                    ) : (
                      'Subscribe'
                    )}
                  </Button>
                  
                  {/* Features List */}
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm">
                        <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                        <span className="text-stone-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Limits Section */}
                  {plan.plan_id === 'free' && (
                    <div className="mt-6 pt-6 border-t border-stone-200">
                      <p className="text-xs uppercase tracking-wider text-stone-400 mb-3">Limitations</p>
                      <ul className="space-y-2 text-xs text-stone-500">
                        <li className="flex items-center gap-2">
                          <X className="w-4 h-4 text-stone-400" /> Max 3 boards
                        </li>
                        <li className="flex items-center gap-2">
                          <X className="w-4 h-4 text-stone-400" /> Max 10 materials per board
                        </li>
                        <li className="flex items-center gap-2">
                          <X className="w-4 h-4 text-stone-400" /> No PDF export
                        </li>
                        <li className="flex items-center gap-2">
                          <X className="w-4 h-4 text-stone-400" /> No team collaboration
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Supplier Plans */}
        {plans.supplier_plans.length > 0 && (
          <div className="mb-20">
            <h2 className="text-sm uppercase tracking-widest text-stone-500 text-center mb-4">
              For Material Suppliers
            </h2>
            <p className="text-center text-stone-600 mb-8 max-w-xl mx-auto">
              Showcase your materials to thousands of architects and designers. Get leads and grow your business.
            </p>
            
            <div className="max-w-lg mx-auto">
              {plans.supplier_plans.map((plan) => {
                const priceInfo = getPrice(plan);
                
                return (
                  <div 
                    key={plan.plan_id}
                    className="bg-gradient-to-br from-stone-900 to-stone-800 text-white rounded-xl p-8"
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                        <Package className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold">{plan.name}</h3>
                        <p className="text-stone-400 text-sm">{plan.description}</p>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold">${plan.pricing.yearly_price}</span>
                        <span className="text-stone-400">/year</span>
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => handleSelectPlan(plan)}
                      disabled={processingPlan === plan.plan_id}
                      className="w-full bg-white text-stone-900 hover:bg-stone-100 mb-6"
                      data-testid="select-supplier-plan"
                    >
                      {processingPlan === plan.plan_id ? 'Processing...' : 'Contact Sales'}
                    </Button>
                    
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm">
                          <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                          <span className="text-stone-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-medium text-center mb-8">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <h3 className="font-medium text-stone-900 mb-2">Can I cancel anytime?</h3>
              <p className="text-stone-600 text-sm">
                Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
              </p>
            </div>
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <h3 className="font-medium text-stone-900 mb-2">What happens after the free trial?</h3>
              <p className="text-stone-600 text-sm">
                After your 14-day trial ends, you'll be automatically moved to the Free plan unless you add a payment method. You won't be charged anything.
              </p>
            </div>
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <h3 className="font-medium text-stone-900 mb-2">Can I upgrade or downgrade my plan?</h3>
              <p className="text-stone-600 text-sm">
                Yes, you can change your plan at any time. When upgrading, you'll be charged the prorated difference. When downgrading, the change takes effect at the end of your billing period.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
